export interface RenderOptions {
  frameBuffer: Uint8Array;
  screenConfig: { cols: number; rows: number };
  inkColor?: { r: number; g: number; b: number };
  statusIcons?: Record<string, boolean>;
  iconPositions?: Record<string, { x: number; y: number }>;
  interfaceImage?: ImageBitmap | null;
  buttons?: Array<{ id: string; x: number; y: number; w: number; h: number; label?: string }>;
  pressedButtons?: Set<string>;
  mode?: 'skeuomorphic' | 'minimal';
  scale?: number;
}

const DEFAULT_INK_COLOR = { r: 0x20, g: 0x20, b: 0x20 };
const DEFAULT_SCALE = 2;
const STATUS_ICON_COLOR = { r: 0x40, g: 0x40, b: 0x40 };
const STATUS_ICON_ON_COLOR = { r: 0x10, g: 0x10, b: 0x10 };
const STATUS_ICON_UNIT_W = 4;
const STATUS_ICON_UNIT_H = 8;

export class Renderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Could not get 2D rendering context');
    }
    this.ctx = ctx;
  }

  render(options: RenderOptions): void {
    const ctx = this.ctx;
    const scale = options.scale ?? DEFAULT_SCALE;

    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    if (options.mode !== 'minimal') {
      this.renderSkeuomorphicOverlay(options.interfaceImage || null, scale);
    }

    const inkColor = options.inkColor ?? DEFAULT_INK_COLOR;
    this.renderLCD(options.frameBuffer, options.screenConfig.cols, options.screenConfig.rows, inkColor, scale);

    if (options.iconPositions) {
      this.renderStatusIcons(
        options.statusIcons ?? {},
        options.iconPositions,
        scale,
      );
    }

    if (options.buttons && options.pressedButtons) {
      if (options.mode === 'minimal') {
        this.renderMinimalButtons(options.buttons, options.pressedButtons, scale, options.screenConfig.cols, options.screenConfig.rows);
      } else {
        this.renderButtonHighlights(options.buttons, options.pressedButtons, scale);
      }
    }
  }

  setSize(width: number, height: number): void {
    this.canvas.width = width;
    this.canvas.height = height;
  }

  captureScreenshot(): Uint8Array {
    try {
      const dataUrl = this.canvas.toDataURL('image/png');
      if (!dataUrl) return new Uint8Array(0);
      const prefix = 'data:image/png;base64,';
      if (dataUrl.startsWith(prefix)) {
        try {
          const base64 = dataUrl.slice(prefix.length);
          const binary = atob(base64);
          const bytes = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
          }
          return bytes;
        } catch {
          return new TextEncoder().encode(dataUrl);
        }
      }
      return new TextEncoder().encode(dataUrl);
    } catch {
      try {
        const w = this.canvas.width;
        const h = this.canvas.height;
        if (w > 0 && h > 0) {
          const imageData = this.ctx.getImageData(0, 0, w, h);
          return new Uint8Array(imageData.data);
        }
      } catch {
      }
      return new Uint8Array(0);
    }
  }

  private renderLCD(
    fb: Uint8Array,
    cols: number,
    rows: number,
    inkColor: { r: number; g: number; b: number },
    scale: number,
  ): void {
    const ctx = this.ctx;
    const pixelWidth = cols * 8;
    const pixelHeight = rows;
    const scaledW = pixelWidth * scale;
    const scaledH = pixelHeight * scale;
    const imageData = ctx.createImageData(scaledW, scaledH);

    for (let row = 0; row < pixelHeight; row++) {
      for (let col = 0; col < pixelWidth; col++) {
        const byteIndex = row * cols + Math.floor(col / 8);
        if (byteIndex >= fb.length) continue;
        const bitIndex = 7 - (col % 8);
        const pixelOn = (fb[byteIndex] >> bitIndex) & 1;

        if (pixelOn) {
          for (let sy = 0; sy < scale; sy++) {
            for (let sx = 0; sx < scale; sx++) {
              const px = (col * scale + sx);
              const py = (row * scale + sy);
              const idx = (py * scaledW + px) * 4;
              imageData.data[idx] = inkColor.r;
              imageData.data[idx + 1] = inkColor.g;
              imageData.data[idx + 2] = inkColor.b;
              imageData.data[idx + 3] = 255;
            }
          }
        } else {
          for (let sy = 0; sy < scale; sy++) {
            for (let sx = 0; sx < scale; sx++) {
              const px = (col * scale + sx);
              const py = (row * scale + sy);
              const idx = (py * scaledW + px) * 4;
              imageData.data[idx] = 240;      // Light gray background
              imageData.data[idx + 1] = 240;
              imageData.data[idx + 2] = 240;
              imageData.data[idx + 3] = 255;  // Opaque — LCD area visible without interface image
            }
          }
        }
      }
    }

    ctx.putImageData(imageData, 0, 0);
  }

  private renderStatusIcons(
    icons: Record<string, boolean>,
    positions: Record<string, { x: number; y: number }>,
    scale: number,
  ): void {
    const ctx = this.ctx;
    const w = STATUS_ICON_UNIT_W * scale;
    const h = STATUS_ICON_UNIT_H * scale;

    for (const [key, pos] of Object.entries(positions)) {
      const on = icons[key] === true;
      const color = on ? STATUS_ICON_ON_COLOR : STATUS_ICON_COLOR;
      ctx.fillStyle = `rgb(${color.r},${color.g},${color.b})`;
      ctx.fillRect(pos.x * scale, pos.y * scale, w, h);
    }
  }

  private renderSkeuomorphicOverlay(image: ImageBitmap | null, scale: number): void {
    if (!image) return;
    const ctx = this.ctx;
    ctx.drawImage(image, 0, 0, image.width * scale, image.height * scale);
  }

  private renderButtonHighlights(
    buttons: Array<{ id: string; x: number; y: number; w: number; h: number }>,
    pressed: Set<string>,
    scale: number,
  ): void {
    const ctx = this.ctx;
    ctx.fillStyle = 'rgba(0, 0, 255, 0.2)';

    for (const btn of buttons) {
      if (pressed.has(btn.id)) {
        ctx.fillRect(
          btn.x * scale,
          btn.y * scale,
          btn.w * scale,
          btn.h * scale,
        );
      }
    }
  }

  private renderMinimalButtons(
    buttons: Array<{ id: string; x: number; y: number; w: number; h: number; label?: string }>,
    pressed: Set<string>,
    scale: number,
    _cols: number,
    rows: number,
  ): void {
    const ctx = this.ctx;
    const colsPerRow = 5;
    const gap = 4 * scale;
    const buttonW = 60 * scale;
    const buttonH = 28 * scale;
    const fontScale = Math.min(scale, 1.5);
    const lcdHeight = rows * scale;
    const startY = lcdHeight + 8 * scale;

    for (let i = 0; i < buttons.length; i++) {
      const btn = buttons[i];
      const gridRow = Math.floor(i / colsPerRow);
      const gridCol = i % colsPerRow;
      const bx = gridCol * (buttonW + gap);
      const by = startY + gridRow * (buttonH + gap);

      const isPressed = pressed.has(btn.id);
      ctx.fillStyle = isPressed ? 'rgba(26, 95, 180, 0.5)' : '#333333';
      ctx.strokeStyle = '#555555';
      ctx.lineWidth = 1;
      ctx.fillRect(bx, by, buttonW, buttonH);
      ctx.strokeRect(bx, by, buttonW, buttonH);

      const label = btn.label || btn.id;
      ctx.fillStyle = isPressed ? '#ffffff' : '#cccccc';
      ctx.font = `${12 * fontScale}px monospace`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, bx + buttonW / 2, by + buttonH / 2);
    }
  }
}
